import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  ButtonProps,
  CardHeader,
  CardTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Icons,
  Label,
  LoadingSpinner,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Progress,
  ScrollArea,
  Separator,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import {
  PiCameraPlusThin,
  PiDotsThreeVertical,
  PiListBulletsThin,
} from 'react-icons/pi';
import { CreateReportTemplate } from '../Reports/Mutations/CreateReportTemplate/CreateReportTemplate';
import {
  Job,
  ReportBackendTypes,
  ReportTemplate,
  useGetReport,
  useGetReportTemplates,
} from '@ais-ui/websockets';
import { reportTypesObject } from '../Reports/reportsTypes';
import React, { useEffect, useState } from 'react';
import { cn, isNonEmpty, useTimeZoneContext } from '@ais-ui/utils';
import { useDateTimeFormat } from '../conversions/useDateFormat';
import { useExportReport } from '../Reports/useExportReport';
import { ReportDelete } from '../Reports/Mutations/DeleteReport';
import { noop } from 'lodash';
import {
  CreateReportProvider,
  useCreateReportContext,
} from '../Reports/Mutations/CreateReportTemplate/CreateReportContext';
import { useReplayRange } from '../../../../../libs/websockets/src/lib/replay-server/useReplayRange';
import { useReplayContext } from './ReplayContext';

interface ReplayVideoItemProps {
  reportTemplate: ReportTemplate;
  report: Job;
}

interface ReplayDropDownMenuProps {
  report: Job;
  onDelete: () => void;
  className?: string;
  id?: string;
}

const ReplayDropDownMenu = ({
  report,
  onDelete,
  className,
  id,
}: ReplayDropDownMenuProps) => {
  const { onExport, activeExportFormat } = useExportReport(report.uuid);
  const downloadDisabled = report.status !== 'FINISHED' || !!activeExportFormat;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild className={className}>
        <Button 
          id={id} 
          size="icon" 
          variant="ghost" 
          className={cn('h-8 w-8')}
        >
          <PiDotsThreeVertical className="h-6 w-6" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          disabled={downloadDisabled}
          onClick={() => onExport('OF_VIDEO')}
        >
          {activeExportFormat ? (
            <Icons.spinner className="h-4 w-4 animate-spin" />
          ) : null}
          Download
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onDelete}>Delete</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

interface ReplayVideoGridItemProps {
  label: string;
  value: string | undefined | null;
}
const ReplayVideoGridItem = ({ label, value }: ReplayVideoGridItemProps) => {
  return (
    <div className="flex flex-col gap-1">
      <p className="text-sm font-medium flex-none">{label}</p>
      <p className="text-sm text-gray-500 dark:text-gray-400 flex-grow text-ellipsis overflow-hidden">
        {isNonEmpty(value) ? value : 'Unknown'}
      </p>
    </div>
  );
};

interface ReplayVideoDetailsProps {
  report: Job;
}

const ReplayVideoDetails = ({ report }: ReplayVideoDetailsProps) => {
  const { timeZone } = useTimeZoneContext();
  const { data: reportDetails, isLoading } = useGetReport(
    report.uuid,
    timeZone
  );
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();

  return (
    <>
      {isLoading && <LoadingSpinner size="small"></LoadingSpinner>}
      {!isLoading && reportDetails && (
        <div className="grid gap-6 px-4 pt-4">
          <div className="grid gap-4">
            <div className="grid grid-cols-2 gap-4">
              <ReplayVideoGridItem
                label="Shape"
                value={reportDetails?.info?.Area}
              />
              <ReplayVideoGridItem
                label="Status"
                value={reportDetails?.info?.Status}
              />
              <ReplayVideoGridItem
                label="Start time"
                value={formatDateTimeWithTimeZone(
                  reportDetails?.info?.['Start time']
                )}
              />
              <ReplayVideoGridItem
                label="End time"
                value={formatDateTimeWithTimeZone(
                  reportDetails?.info?.['End time']
                )}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const ReplayVideoItem = ({ reportTemplate, report }: ReplayVideoItemProps) => {
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();
  const [progress, setProgress] = React.useState<number>(
    report.percentComplete
  );
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const onDelete = () => {
    setShowDeleteDialog(true);
  };

  useEffect(() => {
    const timer = setTimeout(() => setProgress(report.percentComplete), 500);
    return () => clearTimeout(timer);
  }, [report.percentComplete]);

  if (report.status === 'NONE') {
    return null;
  }

  return (
    <AccordionItem       
        key={report.uuid} 
        value={report.uuid} 
        className={cn('px-4')}>
      <div className="w-full flex items-center justify-between">
        <div className="flex-grow">
          <AccordionTrigger className="w-full" hideChevron={true}>
            <div className="flex flex-col w-full pr-4">
              <div className="flex items-center space-x-4">
                <div
                  className={cn(
                    'h-2.5 w-2.5 rounded-full',
                    report.status === 'FINISHED' && 'bg-success',
                    report.status === 'FAILED' && 'bg-destructive',
                    report.status === 'EXECUTING' && 'bg-blue-600',
                    report.status === 'PENDING' && 'bg-slate-600',
                    report.status === 'ABORTED' && 'bg-orange-400'
                  )}
                />
                <div className="flex flex-col items-start">
                  <Label
                    className={cn('text-sm font-medium leading-none text-left')}
                  >
                    {reportTemplate.tplName} - {report.name}
                  </Label>
                  <Label className={cn('text-sm text-muted-foreground')}>
                    {formatDateTimeWithTimeZone(report.creationdate)}
                  </Label>
                </div>
              </div>

              {report.status !== 'FINISHED' &&
                report.status !== 'FAILED' &&
                report.status !== 'FINISHED_SCHEDULED' && (
                  <div className="flex space-x-2 items-center pl-6 pt-1">
                    <p className="w-12">{progress} %</p>
                    <Progress value={progress} className="" />
                  </div>
                )}
            </div>
          </AccordionTrigger>
        </div>
        <div className="flex space-x-2">
          <ReplayDropDownMenu
            id={`replay-video-${report.name || 'unknown'}-${reportTemplate.tplName || 'unknown'}-${report.uuid}-dropdown-button`} 
            //id={reportTemplate.tplName + '-burgerButton'}
            report={report}
            onDelete={onDelete}
          ></ReplayDropDownMenu>
        </div>
        {showDeleteDialog && (
          <ReportDelete
            showDialog={showDeleteDialog}
            setShowDialog={setShowDeleteDialog}
            job={report}
            onBackToReports={noop}
          ></ReportDelete>
        )}
      </div>
      <AccordionContent>
        <ReplayVideoDetails report={report}></ReplayVideoDetails>
      </AccordionContent>
    </AccordionItem>
  );
};

const ReplayVideoList = () => {
  const { data: reportTemplates, isLoading } = useGetReportTemplates(true);
  const [open, setOpen] = useState(false);

  const [replayVideoTemplates, setReplayVideoTemplates] = useState<
    ReportTemplate[]
  >([]);

  useEffect(() => {
    if (reportTemplates) {
      let newReplayVideoTemplates = reportTemplates.filter((reportTemplate) => {
        return reportTemplate.type === ReportBackendTypes.REPLAY_VIDEO;
      });

      newReplayVideoTemplates = newReplayVideoTemplates.sort((a, b) => {
        return (
          new Date(b.tplnewestcreationdate).getTime() -
          new Date(a.tplnewestcreationdate).getTime()
        );
      });

      setReplayVideoTemplates(newReplayVideoTemplates);
    }
  }, [reportTemplates]);

  return (
    <Tooltip>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <TooltipTrigger asChild>
            <Button id="replay-videos-button" size="icon" variant="outline">
              <PiListBulletsThin className="h-6 w-6"></PiListBulletsThin>
            </Button>
          </TooltipTrigger>
        </PopoverTrigger>
        <PopoverContent className="w-96" side="right" align="end">
          <CardHeader className="p-2 space-y-6">
            <div>
              <div className="flex justify-between">
                <CardTitle className="text-lg font-medium pb-2">
                  Replays
                </CardTitle>
                <Button
                  variant="ghost"
                  size="icon"
                  className="-mt-5 -mr-3"
                  onClick={() => setOpen(false)}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                    className="h-4 w-4"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    ></path>
                  </svg>
                </Button>
              </div>
              <Separator></Separator>
            </div>
          </CardHeader>
          <Accordion
            className="px-2 py-1 flex-1 flex flex-col overflow-hidden"
            type="single"
            collapsible
          >
            <ScrollArea className="flex-grow p-0 pmr-4 overflow-y-auto rounded-lg h-80 border">
              {isLoading && <LoadingSpinner></LoadingSpinner>}
              {!isLoading && (
                <div className="">
                  {replayVideoTemplates.map((reportTemplate) => {
                    return reportTemplate.jobs.map((report) => {
                      return (
                        <ReplayVideoItem
                          key={report.uuid}
                          reportTemplate={reportTemplate}
                          report={report}
                        />
                      );
                    });
                  })}
                </div>
              )}
            </ScrollArea>
          </Accordion>
        </PopoverContent>
      </Popover>
      <TooltipContent>List replay videos</TooltipContent>
    </Tooltip>
  );
};

export const ReplayVideo = () => {
  const { data: replayRange } = useReplayRange();

  return (
    <div className="space-x-4">
      <Tooltip>
        <CreateReportProvider
          startDatetimeThreshold={replayRange?.startDatetime}
          endDatetimeThreshold={replayRange?.endDatetime}
        >
          <CreateReportTemplate>
            <TooltipTrigger asChild>
              <ReplayVideoButton></ReplayVideoButton>
            </TooltipTrigger>
          </CreateReportTemplate>
        </CreateReportProvider>
        <TooltipContent>Create a replay video</TooltipContent>
      </Tooltip>
      <ReplayVideoList></ReplayVideoList>
    </div>
  );
};

const ReplayVideoButton = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ onClick, size = 'icon', variant = 'outline', ...props }, ref) => {
    const { setChosenReportType, setDateRange: setReportDateRange } =
      useCreateReportContext();
    const { dateRange: replayDateRange } = useReplayContext();

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (onClick) {
        onClick(e); // Trigger the custom onClick handler if provided
      }
      setChosenReportType(reportTypesObject[ReportBackendTypes.REPLAY_VIDEO]);
      setReportDateRange(replayDateRange);
    };

    return (
      <Button
        id="replay-create-video-button"
        ref={ref}
        size="icon"
        variant="outline"
        onClick={handleClick}
        {...props} // Spread additional props like className, type, etc.
      >
        <PiCameraPlusThin className="h-6 w-6" />
      </Button>
    );
  }
);
